/**
 * A self-contained, free-form Git interpreter for the Sandbox page.
 *
 * Unlike the lesson simulator in `git-sim.ts` (which exposes one pure function
 * per scripted lesson step), this engine parses *arbitrary typed commands* and
 * runs them against an in-memory repository. It is deliberately independent of
 * the lesson engine so the two can evolve separately.
 *
 * The model that makes everything tractable: **every commit carries a full tree
 * snapshot** (`snapshot: filename → content`). With that, checkout, reset,
 * merge (3-way), diff and log are all simple snapshot lookups — no real object
 * store required.
 */
import type { Commit, RepoFile, LogLine } from "./types";

export interface ReflogLine {
  commitId: string;
  action: string;
}

export interface SandboxState {
  initialized: boolean;
  commits: Commit[];
  /** branch name → commit id */
  branches: Record<string, string>;
  /** tag name → commit id */
  tags: Record<string, string>;
  /** current branch name, OR a commit id when HEAD is detached */
  head: string;
  detached: boolean;
  files: Record<string, RepoFile>;
  reflog: ReflogLine[];
  /** in-progress merge awaiting commit (after a conflict or for the message) */
  merging: { sourceLabel: string; sourceTip: string } | null;
  /** simple stash stack (newest last) */
  stash: { message: string; snapshot: Record<string, string> }[];
  config: { name?: string; email?: string };
  remotes: Record<string, string>;
  /** monotonic counter to give commits a deterministic-ish sha + x ordering */
  seq: number;
}

export interface RunResult {
  state: SandboxState;
  lines: LogLine[];
}

// ----------------------------------------------------------------------------
// Utilities
// ----------------------------------------------------------------------------

function clone(s: SandboxState): SandboxState {
  return JSON.parse(JSON.stringify(s));
}

let shaCounter = 0;
function makeSha(seq: number): string {
  // Deterministic-feeling 7-char hex derived from a seq + salt. Avoids
  // Math.random (which is unavailable in some harness contexts) while staying
  // stable across a session.
  shaCounter += 1;
  const n = (seq * 2654435761 + shaCounter * 40503) >>> 0;
  return n.toString(16).padStart(8, "0").slice(0, 7);
}

function ok(text: string): LogLine {
  return { kind: "ok", text };
}
function out(text: string): LogLine {
  return { kind: "out", text };
}
function err(text: string): LogLine {
  return { kind: "err", text };
}
function info(text: string): LogLine {
  return { kind: "info", text };
}

/** Tokenize a command line, honoring single/double quotes and splitting out
 *  the `>` / `>>` redirection operators as their own tokens. */
function tokenize(line: string): string[] {
  const tokens: string[] = [];
  let i = 0;
  const n = line.length;
  while (i < n) {
    const ch = line[i];
    if (ch === " " || ch === "\t") {
      i++;
      continue;
    }
    if (ch === ">") {
      if (line[i + 1] === ">") {
        tokens.push(">>");
        i += 2;
      } else {
        tokens.push(">");
        i += 1;
      }
      continue;
    }
    let tok = "";
    while (i < n && line[i] !== " " && line[i] !== "\t" && line[i] !== ">") {
      const c = line[i];
      if (c === '"' || c === "'") {
        const quote = c;
        i++;
        while (i < n && line[i] !== quote) {
          tok += line[i];
          i++;
        }
        i++; // closing quote
      } else {
        tok += c;
        i++;
      }
    }
    tokens.push(tok);
  }
  return tokens;
}

function findCommit(s: SandboxState, id: string | null | undefined): Commit | undefined {
  if (!id) return undefined;
  return s.commits.find((c) => c.id === id);
}

function headCommitId(s: SandboxState): string | null {
  if (!s.initialized) return null;
  if (s.detached) return s.head;
  return s.branches[s.head] ?? null;
}

function snapshotOf(s: SandboxState, commitId: string | null): Record<string, string> {
  const c = findCommit(s, commitId);
  return c?.snapshot ? { ...c.snapshot } : {};
}

function firstParentWalk(s: SandboxState, id: string, n: number): string | null {
  let cur: Commit | undefined = findCommit(s, id);
  for (let k = 0; k < n; k++) {
    if (!cur || !cur.parent) return null;
    cur = findCommit(s, cur.parent);
  }
  return cur ? cur.id : null;
}

/** Resolve a ref string to a commit id, or null. */
function resolveRef(s: SandboxState, ref: string): string | null {
  if (!ref) return null;
  // HEAD@{n} → reflog
  const reflogMatch = ref.match(/^HEAD@\{(\d+)\}$/);
  if (reflogMatch) {
    const idx = parseInt(reflogMatch[1], 10);
    const realIdx = s.reflog.length - 1 - idx;
    return s.reflog[realIdx]?.commitId ?? null;
  }
  // HEAD / HEAD~n / HEAD^...
  const baseMatch = ref.match(/^(HEAD|[^~^]+)([~^].*)?$/);
  if (baseMatch) {
    const base = baseMatch[1];
    const suffix = baseMatch[2] ?? "";
    let id: string | null = null;
    if (base === "HEAD") id = headCommitId(s);
    else if (s.branches[base]) id = s.branches[base];
    else if (s.tags[base]) id = s.tags[base];
    else {
      // sha prefix (>=4 chars)
      if (base.length >= 4) {
        const matches = s.commits.filter((c) => c.id.startsWith(base));
        if (matches.length === 1) id = matches[0].id;
      }
    }
    if (!id) return null;
    if (!suffix) return id;
    // Walk ~n and ^ markers (first-parent).
    let steps = 0;
    let i = 0;
    while (i < suffix.length) {
      if (suffix[i] === "~") {
        const m = suffix.slice(i + 1).match(/^\d+/);
        const cnt = m ? parseInt(m[0], 10) : 1;
        steps += cnt;
        i += 1 + (m ? m[0].length : 0);
      } else if (suffix[i] === "^") {
        steps += 1;
        i += 1;
      } else {
        i += 1;
      }
    }
    return firstParentWalk(s, id, steps);
  }
  return null;
}

function isAncestor(s: SandboxState, ancestorId: string, descendantId: string): boolean {
  const seen = new Set<string>();
  const stack = [descendantId];
  while (stack.length) {
    const id = stack.pop()!;
    if (id === ancestorId) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    const c = findCommit(s, id);
    if (c?.parent) stack.push(c.parent);
    if (c?.parent2) stack.push(c.parent2);
  }
  return false;
}

function ancestorsOf(s: SandboxState, id: string): Set<string> {
  const set = new Set<string>();
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    if (set.has(cur)) continue;
    set.add(cur);
    const c = findCommit(s, cur);
    if (c?.parent) stack.push(c.parent);
    if (c?.parent2) stack.push(c.parent2);
  }
  return set;
}

function mergeBase(s: SandboxState, a: string, b: string): string | null {
  const ancA = ancestorsOf(s, a);
  const candidates: Commit[] = [];
  const stack = [b];
  const seen = new Set<string>();
  while (stack.length) {
    const cur = stack.pop()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    if (ancA.has(cur)) {
      const c = findCommit(s, cur);
      if (c) candidates.push(c);
      continue; // don't walk past a common ancestor
    }
    const c = findCommit(s, cur);
    if (c?.parent) stack.push(c.parent);
    if (c?.parent2) stack.push(c.parent2);
  }
  if (candidates.length === 0) return null;
  candidates.sort((x, y) => y.x - x.x);
  return candidates[0].id;
}

function reachable(s: SandboxState): Set<string> {
  const roots = [...Object.values(s.branches), ...Object.values(s.tags)];
  const h = headCommitId(s);
  if (h) roots.push(h);
  const set = new Set<string>();
  const stack = [...roots];
  while (stack.length) {
    const id = stack.pop()!;
    if (set.has(id)) continue;
    set.add(id);
    const c = findCommit(s, id);
    if (c?.parent) stack.push(c.parent);
    if (c?.parent2) stack.push(c.parent2);
  }
  return set;
}

/** Recompute working-tree file statuses from staged/committed/content. */
function recomputeStatuses(s: SandboxState): void {
  const snap = snapshotOf(s, headCommitId(s));
  for (const [name, f] of Object.entries(s.files)) {
    if (f.status === "conflicted") continue; // managed explicitly during merge
    const committed = snap[name];
    if (f.staged !== null) {
      f.status = "staged";
    } else if (committed === undefined) {
      f.status = "untracked";
    } else if (f.content !== committed) {
      f.status = "modified";
    } else {
      f.status = "clean";
    }
    f.committed = committed ?? "";
  }
}

/** Load working tree from a commit snapshot (discarding tracked changes). Untracked files kept. */
function loadSnapshotIntoWorkingTree(s: SandboxState, commitId: string | null): void {
  const snap = snapshotOf(s, commitId);
  const next: Record<string, RepoFile> = {};
  for (const [name, f] of Object.entries(s.files)) {
    if (f.status === "untracked") next[name] = f;
  }
  for (const [name, content] of Object.entries(snap)) {
    next[name] = { content, committed: content, staged: null, status: "clean" };
  }
  s.files = next;
}

function hasTrackedChanges(s: SandboxState): boolean {
  return Object.values(s.files).some(
    (f) => f.status === "modified" || f.status === "staged" || f.status === "conflicted",
  );
}

function setReflog(s: SandboxState, commitId: string, action: string): void {
  s.reflog.push({ commitId, action });
}

// ----------------------------------------------------------------------------
// The seed repository (also used by "Reset")
// ----------------------------------------------------------------------------

const SEED_README = `# Atlas

The single workspace your team actually opens every morning.

## Getting started

Edit any file with \`echo\`, stage it, and commit. Try branching!
`;

const SEED_INDEX = `<!doctype html>
<html>
  <head><title>Atlas</title></head>
  <body>
    <h1>Welcome to Atlas</h1>
  </body>
</html>
`;

export function initialSandbox(): SandboxState {
  shaCounter = 0;
  const c1: Commit = {
    id: "a1b2c3d",
    msg: "Initial commit",
    branch: "main",
    parent: null,
    x: 0,
    snapshot: { "README.md": "# Atlas\n" },
  };
  const c2: Commit = {
    id: "e4f5a6b",
    msg: "Add landing page",
    branch: "main",
    parent: "a1b2c3d",
    x: 1,
    snapshot: { "README.md": SEED_README, "index.html": SEED_INDEX },
  };
  return {
    initialized: true,
    commits: [c1, c2],
    branches: { main: "e4f5a6b" },
    tags: {},
    head: "main",
    detached: false,
    files: {
      "README.md": { content: SEED_README, committed: SEED_README, staged: null, status: "clean" },
      "index.html": { content: SEED_INDEX, committed: SEED_INDEX, staged: null, status: "clean" },
    },
    reflog: [
      { commitId: "a1b2c3d", action: "commit (initial): Initial commit" },
      { commitId: "e4f5a6b", action: "commit: Add landing page" },
    ],
    merging: null,
    stash: [],
    config: { name: "Erin", email: "erin@nymbl.app" },
    remotes: {},
    seq: 2,
  };
}

// ----------------------------------------------------------------------------
// Command implementations
// ----------------------------------------------------------------------------

const HELP_LINES: LogLine[] = [
  info("This is a real-ish Git sandbox. Type commands and watch the graph + files react."),
  out(""),
  out("Git commands:"),
  out("  git init                      git status            git add <file|.|-A>"),
  out('  git restore [--staged] <f>    git rm [--cached] <f> git commit -m "msg"'),
  out('  git commit -am "msg"          git log [--oneline]   git branch [-d|-D|-m] [name]'),
  out("  git switch <b> | -c <b>        git checkout [-b] <b> git merge <branch>"),
  out("  git merge --abort             git tag [-d] [name]   git reset [--soft|--mixed|--hard] <ref>"),
  out("  git revert <ref>              git diff [--staged]   git reflog"),
  out('  git stash [pop|list]          git remote [-v|add]   git config user.name "X"'),
  out(""),
  out("Shell helpers:"),
  out("  ls   cat <file>   touch <file>   rm <file>   clear   help"),
  out('  echo "text" > file   (overwrite)        echo "text" >> file   (append)'),
  out(""),
  info("Tip: refs like HEAD~1, a branch name, a tag, a 7-char sha, or HEAD@{2} all work."),
];

function gitInit(s: SandboxState): LogLine[] {
  if (s.initialized) {
    return [out("Reinitialized existing Git repository in /home/erin/my-app/.git/")];
  }
  s.initialized = true;
  s.head = "main";
  s.detached = false;
  s.branches = {};
  return [ok("Initialized empty Git repository in /home/erin/my-app/.git/")];
}

function requireRepo(s: SandboxState): LogLine[] | null {
  if (!s.initialized) {
    return [err("fatal: not a git repository (or any of the parent directories): .git")];
  }
  return null;
}

function gitStatus(s: SandboxState): LogLine[] {
  const lines: LogLine[] = [];
  if (s.detached) {
    lines.push(out(`HEAD detached at ${s.head.slice(0, 7)}`));
  } else {
    lines.push(out(`On branch ${s.head}`));
    if (!s.branches[s.head]) lines.push(out("No commits yet"));
  }
  if (s.merging) {
    lines.push(out(`You have unmerged paths (merging ${s.merging.sourceLabel}).`));
  }

  const staged = Object.entries(s.files).filter(([, f]) => f.status === "staged");
  const modified = Object.entries(s.files).filter(([, f]) => f.status === "modified");
  const untracked = Object.entries(s.files).filter(([, f]) => f.status === "untracked");
  const conflicted = Object.entries(s.files).filter(([, f]) => f.status === "conflicted");

  if (conflicted.length) {
    lines.push(out("Unmerged paths:"));
    lines.push(info('  (use "git add <file>..." to mark resolution)'));
    conflicted.forEach(([n]) => lines.push(err(`\tboth modified:   ${n}`)));
  }
  if (staged.length) {
    lines.push(out("Changes to be committed:"));
    staged.forEach(([n]) => lines.push(ok(`\tmodified:   ${n}`)));
  }
  if (modified.length) {
    lines.push(out("Changes not staged for commit:"));
    lines.push(info('  (use "git add <file>..." to stage)'));
    modified.forEach(([n]) => lines.push({ kind: "out", text: `\tmodified:   ${n}` }));
  }
  if (untracked.length) {
    lines.push(out("Untracked files:"));
    lines.push(info('  (use "git add <file>..." to include in what will be committed)'));
    untracked.forEach(([n]) => lines.push({ kind: "out", text: `\t${n}` }));
  }
  if (!staged.length && !modified.length && !untracked.length && !conflicted.length) {
    lines.push(ok("nothing to commit, working tree clean"));
  }
  return lines;
}

function gitAdd(s: SandboxState, args: string[]): LogLine[] {
  const targets: string[] = [];
  const all = args.includes(".") || args.includes("-A") || args.includes("--all");
  if (all) {
    Object.keys(s.files).forEach((n) => targets.push(n));
  } else {
    args.filter((a) => !a.startsWith("-")).forEach((a) => targets.push(a));
  }
  if (targets.length === 0) {
    return [err("Nothing specified, nothing added.")];
  }
  const lines: LogLine[] = [];
  for (const name of targets) {
    const f = s.files[name];
    if (!f) {
      lines.push(err(`fatal: pathspec '${name}' did not match any files`));
      continue;
    }
    if (f.status === "conflicted") {
      f.staged = f.content;
      f.status = "staged";
      continue;
    }
    if (f.status === "clean") continue;
    f.staged = f.content;
    f.status = "staged";
  }
  recomputeStatuses(s);
  return lines;
}

function gitRm(s: SandboxState, args: string[]): LogLine[] {
  const cached = args.includes("--cached");
  const names = args.filter((a) => !a.startsWith("-"));
  if (names.length === 0) return [err("fatal: No pathspec given to 'git rm'")];
  const lines: LogLine[] = [];
  for (const name of names) {
    const f = s.files[name];
    if (!f) {
      lines.push(err(`fatal: pathspec '${name}' did not match any files`));
      continue;
    }
    if (cached) {
      s.files[name] = { content: f.content, committed: "", staged: null, status: "untracked" };
      lines.push(out(`rm '${name}'`));
    } else {
      delete s.files[name];
      lines.push(out(`rm '${name}'`));
    }
  }
  recomputeStatuses(s);
  return lines;
}

function gitRestore(s: SandboxState, args: string[]): LogLine[] {
  const staged = args.includes("--staged");
  const names = args.filter((a) => !a.startsWith("-"));
  if (names.length === 0) return [err("fatal: you must specify path(s) to restore")];
  const snap = snapshotOf(s, headCommitId(s));
  const lines: LogLine[] = [];
  for (const name of names) {
    const f = s.files[name];
    if (!f) {
      lines.push(err(`error: pathspec '${name}' did not match any file(s)`));
      continue;
    }
    if (staged) {
      f.staged = null;
    } else {
      f.content = snap[name] ?? "";
      f.staged = null;
    }
  }
  recomputeStatuses(s);
  return lines;
}

function gitCommit(s: SandboxState, args: string[]): LogLine[] {
  const flag = args.find((a) => a.startsWith("-") && !a.startsWith("--")) ?? "";
  const autoAdd = /a/.test(flag) || args.includes("-a");
  let msg: string | undefined;
  const mIdx = args.findIndex((a) => a === "-m" || /^-[a-z]*m$/.test(a));
  if (mIdx >= 0) msg = args[mIdx + 1];

  if (autoAdd) {
    for (const f of Object.values(s.files)) {
      if (f.status === "modified") {
        f.staged = f.content;
        f.status = "staged";
      }
    }
  }

  const conflicted = Object.values(s.files).some((f) => f.status === "conflicted");
  if (conflicted) {
    return [err("error: Committing is not possible because you have unmerged files.\nfix them up in the work tree, then run 'git add <file>', then commit.")];
  }

  const stagedEntries = Object.entries(s.files).filter(([, f]) => f.staged !== null);
  const isMerge = !!s.merging;
  if (stagedEntries.length === 0 && !isMerge) {
    return [out('nothing to commit, working tree clean (stage changes with "git add" first)')];
  }
  if (!msg) {
    if (isMerge) {
      msg = `Merge ${s.merging!.sourceLabel} into ${s.detached ? "HEAD" : s.head}`;
    } else {
      return [err('Aborting commit due to empty commit message. Use: git commit -m "your message"')];
    }
  }

  const branch = s.detached ? "HEAD" : s.head;
  const parentId = headCommitId(s);
  const parentCommit = findCommit(s, parentId);
  const prevSnap = snapshotOf(s, parentId);
  const newSnap = { ...prevSnap };
  for (const [name, f] of stagedEntries) {
    newSnap[name] = f.staged!;
  }
  // Files removed from the working tree (and not re-staged) drop out of the tree.
  for (const name of Object.keys(prevSnap)) {
    if (!s.files[name] && !stagedEntries.find(([n]) => n === name)) {
      delete newSnap[name];
    }
  }

  const id = makeSha(s.seq + 1);
  s.seq += 1;
  const parent2 = isMerge ? s.merging!.sourceTip : undefined;
  const xMax = Math.max(parentCommit?.x ?? -1, findCommit(s, parent2 ?? "")?.x ?? -1);
  const commit: Commit = {
    id,
    msg,
    branch,
    parent: parentId,
    parent2,
    x: xMax + 1,
    snapshot: newSnap,
  };
  s.commits.push(commit);
  if (s.detached) s.head = id;
  else s.branches[s.head] = id;

  for (const [, f] of stagedEntries) {
    f.committed = f.staged!;
    f.content = f.staged!;
    f.staged = null;
    f.status = "clean";
  }
  recomputeStatuses(s);
  setReflog(s, id, `${isMerge ? "merge" : "commit"}: ${msg}`);
  const wasMerge = isMerge;
  s.merging = null;

  const lines: LogLine[] = [ok(`[${branch} ${id}] ${msg}`)];
  if (wasMerge) lines.push(out("Merge made by the 'ort' strategy."));
  else lines.push(out(` ${stagedEntries.length} file${stagedEntries.length === 1 ? "" : "s"} changed`));
  return lines;
}

function gitBranch(s: SandboxState, args: string[]): LogLine[] {
  const del = args.includes("-d") || args.includes("-D");
  const rename = args.includes("-m");
  const positional = args.filter((a) => !a.startsWith("-"));

  if (del) {
    const name = positional[0];
    if (!name) return [err("error: branch name required")];
    if (!s.branches[name]) return [err(`error: branch '${name}' not found.`)];
    if (!s.detached && name === s.head) {
      return [err(`error: Cannot delete branch '${name}' checked out at '/home/erin/my-app'`)];
    }
    const tip = s.branches[name];
    delete s.branches[name];
    return [out(`Deleted branch ${name} (was ${tip.slice(0, 7)}).`)];
  }

  if (rename) {
    let oldName = s.head;
    let newName = positional[0];
    if (positional.length === 2) {
      oldName = positional[0];
      newName = positional[1];
    }
    if (!s.branches[oldName]) return [err(`error: refname refs/heads/${oldName} not found`)];
    s.branches[newName] = s.branches[oldName];
    delete s.branches[oldName];
    if (!s.detached && s.head === oldName) s.head = newName;
    return [out(`Renamed branch ${oldName} → ${newName}.`)];
  }

  if (positional.length === 0) {
    const lines: LogLine[] = [];
    if (s.detached) lines.push(ok(`* (HEAD detached at ${s.head.slice(0, 7)})`));
    Object.keys(s.branches)
      .sort()
      .forEach((name) => {
        if (!s.detached && name === s.head) lines.push(ok(`* ${name}`));
        else lines.push(out(`  ${name}`));
      });
    if (lines.length === 0) lines.push(out("No branches yet — make a commit first."));
    return lines;
  }

  const name = positional[0];
  const tip = headCommitId(s);
  if (!tip) return [err(`fatal: not a valid object name: 'HEAD'`)];
  if (s.branches[name]) return [err(`fatal: a branch named '${name}' already exists`)];
  s.branches[name] = tip;
  return [out(`Created branch '${name}' at ${tip.slice(0, 7)}.`)];
}

function createAndSwitch(s: SandboxState, name: string): LogLine[] {
  const tip = headCommitId(s);
  if (!tip) return [err(`fatal: not a valid object name: 'HEAD'`)];
  if (s.branches[name]) return [err(`fatal: a branch named '${name}' already exists`)];
  s.branches[name] = tip;
  s.head = name;
  s.detached = false;
  setReflog(s, tip, `checkout: moving to ${name}`);
  return [ok(`Switched to a new branch '${name}'`)];
}

function switchToBranch(s: SandboxState, name: string): LogLine[] {
  if (!s.branches[name]) return [err(`fatal: invalid reference: ${name}`)];
  if (!s.detached && s.head === name) return [out(`Already on '${name}'`)];
  if (hasTrackedChanges(s)) {
    return [err(
      "error: Your local changes to tracked files would be overwritten by switching branches.\nCommit, stash, or restore them first.",
    )];
  }
  s.head = name;
  s.detached = false;
  loadSnapshotIntoWorkingTree(s, s.branches[name]);
  recomputeStatuses(s);
  setReflog(s, s.branches[name], `checkout: moving to ${name}`);
  return [ok(`Switched to branch '${name}'`)];
}

function gitSwitch(s: SandboxState, args: string[]): LogLine[] {
  const create = args.includes("-c") || args.includes("-C");
  const positional = args.filter((a) => !a.startsWith("-"));
  const name = positional[0];
  if (!name) return [err("fatal: missing branch name")];
  if (create) return createAndSwitch(s, name);
  return switchToBranch(s, name);
}

function gitCheckout(s: SandboxState, args: string[]): LogLine[] {
  const create = args.includes("-b") || args.includes("-B");
  const positional = args.filter((a) => !a.startsWith("-") && a !== "--");
  const name = positional[0];
  if (!name) return [err("error: pathspec or branch required")];
  if (create) return createAndSwitch(s, name);

  if (s.branches[name]) return switchToBranch(s, name);

  if (s.files[name]) return gitRestore(s, [name]);

  const target = resolveRef(s, name);
  if (target) {
    if (hasTrackedChanges(s)) {
      return [err("error: Your local changes would be overwritten by checkout. Commit or restore them first.")];
    }
    s.head = target;
    s.detached = true;
    loadSnapshotIntoWorkingTree(s, target);
    recomputeStatuses(s);
    setReflog(s, target, `checkout: moving to ${name}`);
    const c = findCommit(s, target);
    return [
      info("Note: switching to a detached HEAD."),
      out(`HEAD is now at ${target.slice(0, 7)} ${c?.msg ?? ""}`),
    ];
  }
  return [err(`error: pathspec '${name}' did not match any file(s) or known refs`)];
}

function conflictMarker(oursLabel: string, ours: string, theirsLabel: string, theirs: string): string {
  const o = ours.endsWith("\n") ? ours : ours + "\n";
  const t = theirs.endsWith("\n") ? theirs : theirs + "\n";
  return `<<<<<<< ${oursLabel}\n${o}=======\n${t}>>>>>>> ${theirsLabel}\n`;
}

function gitMerge(s: SandboxState, args: string[]): LogLine[] {
  if (args.includes("--abort")) {
    if (!s.merging) return [out("fatal: There is no merge to abort (MERGE_HEAD missing).")];
    const label = s.merging.sourceLabel;
    s.merging = null;
    loadSnapshotIntoWorkingTree(s, headCommitId(s));
    recomputeStatuses(s);
    return [ok(`Merge aborted (${label}). Working tree restored.`)];
  }
  const positional = args.filter((a) => !a.startsWith("-"));
  const srcName = positional[0];
  if (!srcName) return [err("fatal: No merge source given.")];
  const srcTip = resolveRef(s, srcName);
  if (!srcTip) return [err(`merge: ${srcName} - not something we can merge`)];
  const curTip = headCommitId(s);
  if (!curTip) return [err("fatal: nothing to merge into — no commits yet.")];

  if (srcTip === curTip || isAncestor(s, srcTip, curTip)) {
    return [out("Already up to date.")];
  }

  if (isAncestor(s, curTip, srcTip)) {
    if (!s.detached) s.branches[s.head] = srcTip;
    else s.head = srcTip;
    loadSnapshotIntoWorkingTree(s, srcTip);
    recomputeStatuses(s);
    setReflog(s, srcTip, `merge ${srcName}: Fast-forward`);
    return [
      out(`Updating ${curTip.slice(0, 7)}..${srcTip.slice(0, 7)}`),
      ok(`Fast-forward — ${s.detached ? "HEAD" : s.head} now at ${srcTip.slice(0, 7)}`),
    ];
  }

  const baseId = mergeBase(s, curTip, srcTip);
  const baseSnap = snapshotOf(s, baseId);
  const ourSnap = snapshotOf(s, curTip);
  const theirSnap = snapshotOf(s, srcTip);
  const allFiles = new Set([
    ...Object.keys(baseSnap),
    ...Object.keys(ourSnap),
    ...Object.keys(theirSnap),
  ]);

  const merged: Record<string, string> = {};
  const conflicts: string[] = [];

  for (const name of allFiles) {
    const base = baseSnap[name];
    const ours = ourSnap[name];
    const theirs = theirSnap[name];

    if (ours === theirs) {
      if (ours !== undefined) merged[name] = ours;
      continue;
    }
    if (ours === base) {
      if (theirs !== undefined) merged[name] = theirs;
      continue;
    }
    if (theirs === base) {
      if (ours !== undefined) merged[name] = ours;
      continue;
    }
    conflicts.push(name);
    merged[name] = conflictMarker("HEAD", ours ?? "", srcName, theirs ?? "");
  }

  const nextFiles: Record<string, RepoFile> = {};
  for (const [name, f] of Object.entries(s.files)) {
    if (f.status === "untracked") nextFiles[name] = f;
  }
  for (const [name, content] of Object.entries(merged)) {
    const isConflict = conflicts.includes(name);
    const ours = ourSnap[name];
    // Only files the merge actually changed get staged; files identical to our
    // side stay clean so `git status` shows just the real merge result.
    const changed = content !== ours;
    nextFiles[name] = {
      content,
      committed: ours ?? "",
      staged: isConflict ? null : changed ? content : null,
      status: isConflict ? "conflicted" : changed ? "staged" : "clean",
    };
  }
  s.files = nextFiles;
  s.merging = { sourceLabel: srcName, sourceTip: srcTip };

  if (conflicts.length) {
    const lines: LogLine[] = [];
    conflicts.forEach((n) => lines.push(out(`Auto-merging ${n}`)));
    conflicts.forEach((n) => lines.push(err(`CONFLICT (content): Merge conflict in ${n}`)));
    lines.push(info("Automatic merge failed; fix conflicts and then commit the result."));
    lines.push(info("Edit the file(s) to remove the <<<<<<< markers, then: git add <file> && git commit"));
    return lines;
  }

  return gitCommit(s, []);
}

function gitTag(s: SandboxState, args: string[]): LogLine[] {
  const del = args.includes("-d");
  const positional = args.filter((a) => !a.startsWith("-"));
  if (del) {
    const name = positional[0];
    if (!s.tags[name]) return [err(`error: tag '${name}' not found.`)];
    delete s.tags[name];
    return [out(`Deleted tag '${name}'`)];
  }
  if (positional.length === 0) {
    const names = Object.keys(s.tags).sort();
    if (names.length === 0) return [out("No tags yet.")];
    return names.map((n) => out(n));
  }
  const name = positional[0];
  const ref = positional[1];
  const target = ref ? resolveRef(s, ref) : headCommitId(s);
  if (!target) return [err(`fatal: Failed to resolve '${ref ?? "HEAD"}' as a valid ref.`)];
  s.tags[name] = target;
  return [out(`Created tag '${name}' at ${target.slice(0, 7)}.`)];
}

function gitLog(s: SandboxState, args: string[]): LogLine[] {
  const oneline = args.includes("--oneline");
  const all = args.includes("--all");
  const tip = headCommitId(s);
  if (!tip) return [out("fatal: your current branch does not have any commits yet")];

  const ids = all ? reachable(s) : ancestorsOf(s, tip);
  const commits = s.commits.filter((c) => ids.has(c.id)).sort((a, b) => b.x - a.x);

  const decoMap: Record<string, string[]> = {};
  for (const [name, id] of Object.entries(s.branches)) (decoMap[id] ??= []).push(name);
  for (const [name, id] of Object.entries(s.tags)) (decoMap[id] ??= []).push(`tag: ${name}`);
  const headId = headCommitId(s);

  const lines: LogLine[] = [];
  for (const c of commits) {
    const deco = decoMap[c.id] ? [...decoMap[c.id]] : [];
    if (c.id === headId) deco.unshift(s.detached ? "HEAD" : `HEAD -> ${s.head}`);
    const decoStr = deco.length ? ` (${deco.join(", ")})` : "";
    if (oneline) {
      lines.push(out(`${c.id.slice(0, 7)}${decoStr} ${c.msg}`));
    } else {
      lines.push(info(`commit ${c.id}${decoStr}`));
      lines.push(out(`Author: ${s.config.name ?? "You"} <${s.config.email ?? "you@example.com"}>`));
      lines.push(out(`    ${c.msg}`));
      lines.push(out(""));
    }
  }
  return lines;
}

function gitReset(s: SandboxState, args: string[]): LogLine[] {
  let mode: "soft" | "mixed" | "hard" = "mixed";
  if (args.includes("--soft")) mode = "soft";
  else if (args.includes("--hard")) mode = "hard";
  const positional = args.filter((a) => !a.startsWith("-"));

  // `git reset` / `git reset <file>` (unstage) when no resolvable ref.
  if (positional.length === 0 || (s.files[positional[0]] && !resolveRef(s, positional[0]))) {
    const names = positional.length ? positional : Object.keys(s.files);
    for (const name of names) {
      const f = s.files[name];
      if (f && f.staged !== null) f.staged = null;
    }
    recomputeStatuses(s);
    return [out("Unstaged changes after reset.")];
  }

  const ref = positional[0];
  const target = resolveRef(s, ref);
  if (!target) return [err(`fatal: ambiguous argument '${ref}': unknown revision`)];

  if (s.detached) s.head = target;
  else s.branches[s.head] = target;

  if (mode === "mixed" || mode === "hard") {
    for (const f of Object.values(s.files)) if (f.staged !== null) f.staged = null;
  }
  if (mode === "hard") loadSnapshotIntoWorkingTree(s, target);
  recomputeStatuses(s);
  setReflog(s, target, `reset: moving to ${ref}`);

  const c = findCommit(s, target);
  const branch = s.detached ? "HEAD" : s.head;
  const msg =
    mode === "hard"
      ? `HEAD is now at ${target.slice(0, 7)} ${c?.msg ?? ""}`
      : mode === "soft"
        ? `Moved ${branch} to ${target.slice(0, 7)}. Staging + working tree kept.`
        : `Moved ${branch} to ${target.slice(0, 7)}. Staging cleared; working tree kept.`;
  return [ok(msg)];
}

function gitRevert(s: SandboxState, args: string[]): LogLine[] {
  const positional = args.filter((a) => !a.startsWith("-"));
  const ref = positional[0];
  const target = resolveRef(s, ref);
  if (!target) return [err(`fatal: bad revision '${ref}'`)];
  const targetCommit = findCommit(s, target);
  if (!targetCommit) return [err(`fatal: bad revision '${ref}'`)];
  const parentSnap = snapshotOf(s, targetCommit.parent);
  const targetSnap = snapshotOf(s, target);
  const curTip = headCommitId(s);
  const curSnap = snapshotOf(s, curTip);

  const newSnap = { ...curSnap };
  const touched = new Set([...Object.keys(parentSnap), ...Object.keys(targetSnap)]);
  for (const name of touched) {
    const before = parentSnap[name];
    if (before === undefined) delete newSnap[name];
    else newSnap[name] = before;
  }

  const id = makeSha(s.seq + 1);
  s.seq += 1;
  const curCommit = findCommit(s, curTip);
  const branch = s.detached ? "HEAD" : s.head;
  const msg = `Revert "${targetCommit.msg}"`;
  const commit: Commit = {
    id,
    msg,
    branch,
    parent: curTip,
    x: (curCommit?.x ?? 0) + 1,
    snapshot: newSnap,
  };
  s.commits.push(commit);
  if (s.detached) s.head = id;
  else s.branches[s.head] = id;
  loadSnapshotIntoWorkingTree(s, id);
  recomputeStatuses(s);
  setReflog(s, id, `revert: ${msg}`);
  return [ok(`[${branch} ${id}] ${msg}`)];
}

function gitDiff(s: SandboxState, args: string[]): LogLine[] {
  const staged = args.includes("--staged") || args.includes("--cached");
  const snap = snapshotOf(s, headCommitId(s));
  const lines: LogLine[] = [];
  for (const [name, f] of Object.entries(s.files)) {
    const committed = snap[name] ?? "";
    const left = staged ? committed : f.staged ?? committed;
    const right = staged ? f.staged ?? committed : f.content;
    if (left === right) continue;
    const diff = lineDiff(left, right);
    if (diff.length === 0) continue;
    lines.push(info(`diff --git a/${name} b/${name}`));
    lines.push(info(`--- a/${name}`));
    lines.push(info(`+++ b/${name}`));
    lines.push(...diff);
  }
  if (lines.length === 0) lines.push(out("No changes."));
  return lines;
}

/** Minimal prefix/suffix-trimmed line diff. Good enough to teach the idea. */
function lineDiff(oldText: string, newText: string): LogLine[] {
  const a = oldText.split("\n");
  const b = newText.split("\n");
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length - 1;
  let endB = b.length - 1;
  while (endA >= start && endB >= start && a[endA] === b[endB]) {
    endA--;
    endB--;
  }
  const lines: LogLine[] = [];
  for (let i = start; i <= endA; i++) lines.push(err(`-${a[i]}`));
  for (let i = start; i <= endB; i++) lines.push(ok(`+${b[i]}`));
  return lines;
}

function gitReflog(s: SandboxState): LogLine[] {
  if (s.reflog.length === 0) return [out("No reflog entries.")];
  const lines: LogLine[] = [];
  for (let i = s.reflog.length - 1; i >= 0; i--) {
    const e = s.reflog[i];
    const idx = s.reflog.length - 1 - i;
    lines.push(out(`${e.commitId.slice(0, 7)} HEAD@{${idx}}: ${e.action}`));
  }
  return lines;
}

function gitStash(s: SandboxState, args: string[]): LogLine[] {
  const sub = args[0];
  if (sub === "list") {
    if (s.stash.length === 0) return [out("No stash entries.")];
    return s.stash
      .map((e, i) => out(`stash@{${s.stash.length - 1 - i}}: ${e.message}`))
      .reverse();
  }
  if (sub === "pop" || sub === "apply") {
    const entry = s.stash.pop();
    if (!entry) return [err("No stash entries to pop.")];
    for (const [name, content] of Object.entries(entry.snapshot)) {
      const f = s.files[name];
      if (f) f.content = content;
      else s.files[name] = { content, committed: "", staged: null, status: "untracked" };
    }
    recomputeStatuses(s);
    return [ok(`Applied ${entry.message}`)];
  }
  if (!hasTrackedChanges(s)) return [out("No local changes to save")];
  const snap: Record<string, string> = {};
  for (const [name, f] of Object.entries(s.files)) {
    if (f.status === "modified" || f.status === "staged") snap[name] = f.content;
  }
  const headSnap = snapshotOf(s, headCommitId(s));
  const label = s.detached ? "HEAD" : s.head;
  s.stash.push({ message: `WIP on ${label}`, snapshot: snap });
  for (const [name, f] of Object.entries(s.files)) {
    if (f.status === "modified" || f.status === "staged") {
      f.content = headSnap[name] ?? "";
      f.staged = null;
    }
  }
  recomputeStatuses(s);
  return [ok(`Saved working directory and index state WIP on ${label}`)];
}

function gitConfig(s: SandboxState, args: string[]): LogLine[] {
  const key = args.find((a) => !a.startsWith("-"));
  const valIdx = args.findIndex((a) => a === key);
  const val = args[valIdx + 1];
  if (key === "user.name") {
    if (val === undefined) return [out(s.config.name ?? "")];
    s.config.name = val;
    return [];
  }
  if (key === "user.email") {
    if (val === undefined) return [out(s.config.email ?? "")];
    s.config.email = val;
    return [];
  }
  return [out(`(sandbox) config '${key ?? ""}' noted.`)];
}

function gitRemote(s: SandboxState, args: string[]): LogLine[] {
  if (args[0] === "add") {
    const name = args[1];
    const url = args[2];
    if (!name || !url) return [err("usage: git remote add <name> <url>")];
    s.remotes[name] = url;
    return [];
  }
  const verbose = args.includes("-v");
  const names = Object.keys(s.remotes);
  if (names.length === 0) return [out("(no remotes configured)")];
  if (verbose) {
    const lines: LogLine[] = [];
    names.forEach((n) => {
      lines.push(out(`${n}\t${s.remotes[n]} (fetch)`));
      lines.push(out(`${n}\t${s.remotes[n]} (push)`));
    });
    return lines;
  }
  return names.map((n) => out(n));
}

function notSupported(cmd: string): LogLine[] {
  return [
    info(`'${cmd}' isn't simulated in this sandbox — it needs a real remote.`),
    info("This playground focuses on local history: commit, branch, merge, reset, revert."),
  ];
}

// ---- shell helpers ----------------------------------------------------------

function setWorkingContent(s: SandboxState, file: string, content: string): void {
  const existing = s.files[file];
  if (existing) {
    existing.content = content;
    if (existing.status === "conflicted" && !content.includes("<<<<<<<")) {
      existing.status = "modified";
    }
  } else {
    s.files[file] = { content, committed: "", staged: null, status: "untracked" };
  }
}

function shEcho(s: SandboxState, tokens: string[]): LogLine[] {
  const redirIdx = tokens.findIndex((t) => t === ">" || t === ">>");
  if (redirIdx === -1) return [out(tokens.slice(1).join(" "))];
  const text = tokens.slice(1, redirIdx).join(" ");
  const op = tokens[redirIdx];
  const file = tokens[redirIdx + 1];
  if (!file) return [err("syntax error near unexpected token `newline'")];
  const content = text + "\n";
  if (op === ">>") {
    const base = s.files[file] ? s.files[file].content : "";
    setWorkingContent(s, file, base + content);
  } else {
    setWorkingContent(s, file, content);
  }
  recomputeStatuses(s);
  return [];
}

function shCat(s: SandboxState, file: string): LogLine[] {
  const f = s.files[file];
  if (!f) return [err(`cat: ${file}: No such file or directory`)];
  return f.content.replace(/\n$/, "").split("\n").map((l) => out(l));
}

function shLs(s: SandboxState): LogLine[] {
  const names = Object.keys(s.files).sort();
  if (names.length === 0) return [out("(empty directory)")];
  return [out(names.join("  "))];
}

function shTouch(s: SandboxState, file: string): LogLine[] {
  if (!file) return [err("touch: missing file operand")];
  if (!s.files[file]) s.files[file] = { content: "", committed: "", staged: null, status: "untracked" };
  recomputeStatuses(s);
  return [];
}

function shRm(s: SandboxState, file: string): LogLine[] {
  if (!file) return [err("rm: missing operand")];
  if (!s.files[file]) return [err(`rm: cannot remove '${file}': No such file or directory`)];
  delete s.files[file];
  recomputeStatuses(s);
  return [];
}

/** Edit a file's working-tree content directly (used by the in-page editor). */
export function editWorkingFile(prev: SandboxState, name: string, content: string): SandboxState {
  const s = clone(prev);
  setWorkingContent(s, name, content);
  recomputeStatuses(s);
  return s;
}

// ----------------------------------------------------------------------------
// Dispatcher
// ----------------------------------------------------------------------------

export function run(prev: SandboxState, rawLine: string): RunResult {
  const line = rawLine.trim();
  if (!line) return { state: prev, lines: [] };
  const s = clone(prev);
  const tokens = tokenize(line);
  const cmd = tokens[0];

  if (cmd === "help" || cmd === "--help") return { state: prev, lines: HELP_LINES };
  if (cmd === "clear" || cmd === "cls") return { state: prev, lines: [{ kind: "info", text: "__CLEAR__" }] };
  if (cmd === "pwd") return { state: prev, lines: [out("/home/erin/my-app")] };
  if (cmd === "ls" || cmd === "dir") return { state: prev, lines: shLs(s) };
  if (cmd === "echo") return { state: s, lines: shEcho(s, tokens) };
  if (cmd === "cat") return { state: prev, lines: shCat(s, tokens[1]) };
  if (cmd === "touch") return { state: s, lines: shTouch(s, tokens[1]) };
  if (cmd === "rm") return { state: s, lines: shRm(s, tokens[1]) };

  if (cmd !== "git") {
    return {
      state: prev,
      lines: [err(`command not found: ${cmd}`), info('Type "help" to see what this sandbox understands.')],
    };
  }

  const sub = tokens[1];
  const args = tokens.slice(2);

  if (sub === "init") return { state: s, lines: gitInit(s) };

  const repoErr = requireRepo(s);
  if (repoErr) return { state: prev, lines: repoErr };

  switch (sub) {
    case "status":
      return { state: prev, lines: gitStatus(s) };
    case "add":
      return { state: s, lines: gitAdd(s, args) };
    case "rm":
      return { state: s, lines: gitRm(s, args) };
    case "restore":
      return { state: s, lines: gitRestore(s, args) };
    case "commit":
      return { state: s, lines: gitCommit(s, args) };
    case "branch":
      return { state: s, lines: gitBranch(s, args) };
    case "switch":
      return { state: s, lines: gitSwitch(s, args) };
    case "checkout":
      return { state: s, lines: gitCheckout(s, args) };
    case "merge":
      return { state: s, lines: gitMerge(s, args) };
    case "tag":
      return { state: s, lines: gitTag(s, args) };
    case "log":
      return { state: prev, lines: gitLog(s, args) };
    case "reset":
      return { state: s, lines: gitReset(s, args) };
    case "revert":
      return { state: s, lines: gitRevert(s, args) };
    case "diff":
      return { state: prev, lines: gitDiff(s, args) };
    case "reflog":
      return { state: prev, lines: gitReflog(s) };
    case "stash":
      return { state: s, lines: gitStash(s, args) };
    case "config":
      return { state: s, lines: gitConfig(s, args) };
    case "remote":
      return { state: s, lines: gitRemote(s, args) };
    case "push":
    case "pull":
    case "fetch":
    case "clone":
    case "rebase":
    case "cherry-pick":
      return { state: prev, lines: notSupported(`git ${sub}`) };
    default:
      return {
        state: prev,
        lines: [err(`git: '${sub ?? ""}' is not a git command this sandbox knows.`), info('Type "help".')],
      };
  }
}
